export interface SeriesTitleInput {
  eventId: string;
  /** 1-based position within the series (API-provided `seriesIndex`). */
  seriesIndex?: number | null;
  /** Total number of events in the series (API-provided `seriesTotal`). */
  seriesTotal?: number | null;
}

/**
 * Appends the multi-day series occurrence suffix (e.g. `" (1/2)"`) to a title.
 *
 * The `seriesIndex`/`seriesTotal` values come directly from the event API and
 * are the single source of truth for the suffix. The suffix is only shown for
 * genuine multi-event series (`seriesTotal >= 2`); events missing either field
 * or belonging to a single-occurrence series keep their base title unchanged.
 */
export function formatSeriesOrdinalTitle(
  baseTitle: string,
  seriesIndex?: number | null,
  seriesTotal?: number | null
): string {
  if (typeof seriesIndex === 'number' && typeof seriesTotal === 'number' && seriesTotal >= 2) {
    return `${baseTitle} (${seriesIndex}/${seriesTotal})`;
  }
  return baseTitle;
}

/**
 * Builds a map of `eventId → display title` where series events carry the
 * `(index/total)` occurrence suffix, sourced from each event's API-provided
 * `seriesIndex`/`seriesTotal`. Used across event title surfaces (dashboard
 * cards, planner rows, calendars).
 */
export function buildSeriesOrdinalTitleMap<T extends SeriesTitleInput>(
  events: readonly T[],
  getBaseTitle: (event: T) => string
): ReadonlyMap<string, string> {
  const titles = new Map<string, string>();

  for (const event of events) {
    titles.set(
      event.eventId,
      formatSeriesOrdinalTitle(getBaseTitle(event), event.seriesIndex, event.seriesTotal)
    );
  }

  return titles;
}
